import React, { useState } from 'react';
import { DevicesStoreInfoResponse } from '../../../../../service/store/StoreService.model';
import { useNavigate } from 'react-router-dom';
import PaperButton from '../../../../../theme/ui/Atoms/PaperButton/PaperButton';
import { CircularProgress, Typography } from '@mui/material';
import InputSelect from '../../../../../theme/ui/Atoms/InputSelect';
import { AIR_CONNECT_NAME_OPTIONS } from '../../../../../service/device/CommanderAirConnectAPI/CommanderAirConnectAPI.constants';
import DeviceType from '../../../InstallFacilities/DevicesManagerOverviewSetUp/DeviceType';
import StatusOverviewByDevice from './StatusOverviewByDevice';
import { RSSIOverviewByDevice } from './RSSIOverviewByDevice';
import { DeviceActions } from './DeviceActions';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import FlexeserveDeviceAPI from '../../../../../service/device/FlexeserveDevice/FlexeserveDeviceAPI';
import useErrorMessage from '../../../../../shared/hooks/useErrorMessage';
import useMessage from '../../../../../shared/hooks/useMessage';
import GroupAPI, {
  useGetGroupByIdQuery,
} from '../../../../../service/group/GroupAPI';
import { GroupWithComposableDevice } from '../../../../../service/group/group.model';
import Badge from '../../../../../theme/ui/Atoms/Badge/Badge';
import { AlarmsDevice } from './AlarmsDevice';

interface DeviceUnitProps {
  device: DevicesStoreInfoResponse;
  deviceEditing: string | null;
  setDeviceEditing: (editing: string | null) => void;
  duplicated?:
    | {
        id: string;
        type: string;
        label: string;
      }
    | undefined;
  schedules: {
    optionsSchedule: { text: string; value: string }[];
    isLoadingSchedules?: boolean;
    isFetchingSchedules?: boolean;
    displaySchedule?: boolean;
  };
}

export const DeviceUnit = (props: DeviceUnitProps) => {
  const { device, deviceEditing, setDeviceEditing, duplicated, schedules } =
    props;
  const group = useGetGroupByIdQuery(device.composableDeviceId ?? '', {
    skip: !device.composableDeviceId,
  }).data as GroupWithComposableDevice | undefined;
  const {
    optionsSchedule,
    isLoadingSchedules,
    isFetchingSchedules,
    displaySchedule,
  } = schedules;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [displayRemoveDeviceModal, setDisplayRemoveDeviceModal] =
    useState(false);

  const [updateFlexeserveDevice, { isLoading: isUpdatingDevice }] =
    FlexeserveDeviceAPI.useUpdateSetupDeviceMutation();
  const [updateComposableDevice, { isLoading: isUpdatingGroup }] =
    GroupAPI.useUpdateGroupMutation();

  const onClickDevice = (device: DevicesStoreInfoResponse) => {
    if (deviceEditing !== null || displayRemoveDeviceModal) return;
    navigate(`/manager/store/${device.storeId}/device/${device.uuid}/actions`);
  };

  const validationSchema = yup.object({
    unitName: yup.string().required('initial_setup.device_name_required'),
    machineTypeId: yup.string().required('initial_setup.device_name_required'),
  });

  const showErrorMessage = useErrorMessage();
  const showMessage = useMessage();

  const formik = useFormik({
    initialValues: {
      unitName: device.unitName,
      machineTypeId: group?.composableDevice.machineTypeId ?? '',
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values) => {
      const composableDevice = {
        id: device.composableDeviceId ?? '',
        data: {
          name: device.unitName,
          composableDevice: {
            ...group?.composableDevice,
            channels: group?.composableDevice.channels ?? [],
            labels: group?.composableDevice.labels ?? {},
            controlUnits: group?.composableDevice.controlUnits ?? [],
            machineTypeId: values.machineTypeId,
          },
        },
      };

      return Promise.all([
        updateFlexeserveDevice({ ...device, unitName: values.unitName }),
        updateComposableDevice(composableDevice),
      ])
        .then(() => showMessage(t('device.saved_successful_message')))
        .catch(showErrorMessage)
        .finally(() => setDeviceEditing(null));
    },
  });

  const isEditing = deviceEditing === device.uuid;
  const isDeviceSelected = deviceEditing !== null;

  return (
    <PaperButton
      component={'tr'}
      key={device.uuid}
      elevation={undefined}
      onClick={() => onClickDevice(device)}
      sx={{
        opacity: isEditing || !isDeviceSelected ? 1 : 0.4,
        cursor: isEditing || !isDeviceSelected ? 'pointer' : 'default',
      }}
    >
      <td style={{ position: 'relative', height: '80px', minWidth: '210px' }}>
        {duplicated ? (
          <Badge type={duplicated.type}>
            <Typography>{t(duplicated.label)}</Typography>
          </Badge>
        ) : null}
        <Typography
          variant="body1"
          textAlign={'left'}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '200px',
            display: 'block',
            position: 'relative',
          }}
        >
          {!isEditing ? (
            device.unitName
          ) : (
            <InputSelect
              select
              id={`${device.uuid}-name`}
              name={`${device.uuid}-name`}
              options={AIR_CONNECT_NAME_OPTIONS?.map((name) => ({
                value: name,
                text: name,
              }))}
              placeholder={t('initial_setup.scan_air_connect.device_name')}
              fullWidth
              required
              aria-required
              value={formik.values.unitName}
              onChange={(v: string) => formik.setFieldValue('unitName', v)}
              onBlur={formik.handleBlur}
            />
          )}
        </Typography>
      </td>
      <td>
        <Typography variant="body1" textAlign={'center'}>
          <DeviceType
            device={device}
            isEditing={isEditing}
            onSelectType={(type) => formik.setFieldValue('machineTypeId', type)}
            formik={formik}
          />
        </Typography>
      </td>
      {!!displaySchedule && (
        <td>
          <Typography variant="body1" textAlign={'center'}>
            {isLoadingSchedules || isFetchingSchedules ? (
              <CircularProgress size={'1em'} />
            ) : (
              <InputSelect
                id={`${device.uuid}-schedule`}
                name={`${device.uuid}-schedule`}
                options={optionsSchedule}
                value={device.schedule}
                disabled={isDeviceSelected && !isEditing}
                onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                  e.stopPropagation();
                  navigate(
                    `/manager/store/${device.storeId}/device/${device.uuid}/schedule`
                  );
                }}
                fullWidth
              />
            )}
          </Typography>
        </td>
      )}
      <td>
        <StatusOverviewByDevice device={device} />
      </td>
      <td>
        <AlarmsDevice device={device} />
      </td>
      <td>
        <RSSIOverviewByDevice device={device} />
      </td>
      <td>
        <DeviceActions
          device={device}
          editingDeviceId={deviceEditing}
          isEditing={isDeviceSelected}
          setIsEditing={(editing) =>
            setDeviceEditing(editing ? device.uuid : null)
          }
          onSave={() => formik.handleSubmit()}
          isSaving={isUpdatingDevice || isUpdatingGroup}
          displayRemoveDeviceModal={displayRemoveDeviceModal}
          setDisplayRemoveDeviceModal={setDisplayRemoveDeviceModal}
          canRemoveNode={duplicated?.type === 'duplicated' ? false : true}
        />
      </td>
    </PaperButton>
  );
};
